import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  TextField,
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import {
  useContext, useState, useEffect, ChangeEvent,
} from 'react'
import { useSelector } from '@xstate/react'
import { LmnContext } from 'src/components/Dialogs/Lmn/LmnDialog'
import LmnBackButton from 'src/components/Dialogs/Lmn/common/LmnBackButton'
import { getCustomerInfo } from 'src/helpers/lmnHelpers'
import styles from 'src/components/Lmn/Lmn.scss'
import { CDN_PATH } from 'src/constants/global'
import mixpanel from 'mixpanel-browser'

const NO_VALUE = 'No'
const YES_VALUE = 'Yes'

const hasNoValueOrEmpty = (value: string): boolean => (
  (value === NO_VALUE) || (value === '')
)

const LmnMedicalProfile = (): JSX.Element => {
  const { lmnContextService } = useContext(LmnContext)
  const { send } = lmnContextService

  const {
    hasSelfReportedMeds,
    hasAllergiesOrReactions,
    hasMedicalHistory,
    hasOtherQuestions,
    A1,
    A2,
    A3,
    Q1,
    Q2,
    Q3,
    selfReportedMeds: customerSelfReportedMeds,
    allergies: customerAllergies,
    medicalConditions: customerMedicalConditions,
  } = useSelector(lmnContextService, getCustomerInfo)
  const [preliminaryAssessmentConditions, setPreliminaryAssessmentConditions] = useState('')
  const [medicalHistory, setMedicalHistory] = useState('')
  const [allergiesOrReactions, setAllergiesOrReactions] = useState('')
  const [selfReportedMeds, setSelfReportedMeds] = useState('')
  const [otherQuestions, setOtherQuestions] = useState('')
  const [error, setError] = useState(false)
  const [existingMedicalRecord, setExistingMedicalRecord] = useState('')

  useEffect(() => {
    mixpanel.track('view_lmn_medical_profile')
  }, [])

  const handleSelectChanged = (event: SelectChangeEvent, field: string): void => {
    const fieldValue = event.target.value
    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        [field]: fieldValue,
      },
    })
  }

  const handleSetPreliminaryAssessmentConditions = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const fieldValue = event.target.value
    setPreliminaryAssessmentConditions(fieldValue)
    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        A1: fieldValue.trim(),
      },
    })
  }

  const handleSetExistingMedicalRecord = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const fieldValue = event.target.value
    setExistingMedicalRecord(fieldValue)
    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        A3: fieldValue.trim(),
      },
    })
  }

  const handleSetSelfReportedMeds = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const fieldValue = event.target.value
    setSelfReportedMeds(fieldValue)
    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        selfReportedMeds: hasSelfReportedMeds === YES_VALUE ? fieldValue.trim() : NO_VALUE,
      },
    })
  }

  const handleSetAllergiesOrReactions = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const fieldValue = event.target.value
    setAllergiesOrReactions(fieldValue)
    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        allergies: hasAllergiesOrReactions === YES_VALUE ? fieldValue.trim() : NO_VALUE,
      },
    })
  }

  const handleSetMedicalHistory = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const fieldValue = event.target.value
    setMedicalHistory(fieldValue)
    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        medicalConditions: hasMedicalHistory === YES_VALUE ? fieldValue.trim() : NO_VALUE,
      },
    })
  }

  const handleSetOtherQuestions = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const fieldValue = event.target.value
    setOtherQuestions(fieldValue)
    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        A2: hasOtherQuestions === YES_VALUE ? fieldValue.trim() : NO_VALUE,
      },
    })
  }

  const preliminaryAssessmentConditionsInvalid = preliminaryAssessmentConditions === ''
  const existingMedicalRecordInvalid = existingMedicalRecord === ''
  const medicalHistoryInvalid = (hasMedicalHistory === YES_VALUE) && medicalHistory === ''
  const allergiesOrReactionsInvalid = (hasAllergiesOrReactions === YES_VALUE) && allergiesOrReactions === ''
  const selfReportedMedsInvalid = (hasSelfReportedMeds === YES_VALUE) && selfReportedMeds === ''
  const otherQuestionsInvalid = (hasOtherQuestions === YES_VALUE) && otherQuestions === ''

  const handleNextStepClicked = (): void => {
    if (preliminaryAssessmentConditionsInvalid || medicalHistoryInvalid || existingMedicalRecordInvalid
      || allergiesOrReactionsInvalid || selfReportedMedsInvalid || otherQuestionsInvalid) {
      setError(true)
      return
    }

    setError(false)
    mixpanel.track('completed_lmn_medical_profile')
    send({
      type: 'MEDICAL_PROFILE_COMPLETE',
      data: {
        selfReportedMeds: hasSelfReportedMeds === YES_VALUE ? selfReportedMeds.trim() : NO_VALUE,
        allergies: hasAllergiesOrReactions === YES_VALUE ? allergiesOrReactions.trim() : NO_VALUE,
        medicalConditions: hasMedicalHistory === YES_VALUE ? medicalHistory.trim() : NO_VALUE,
        Q1,
        A1: preliminaryAssessmentConditions.trim(),
        Q2,
        A2: hasOtherQuestions === YES_VALUE ? otherQuestions.trim() : NO_VALUE,
        Q3,
        A3: existingMedicalRecord.trim(),
      },
    })
  }

  useEffect(() => {
    // Repopulating info if they go back to this screen
    setPreliminaryAssessmentConditions(A1)
    setSelfReportedMeds(hasNoValueOrEmpty(customerSelfReportedMeds) ? '' : customerSelfReportedMeds)
    setAllergiesOrReactions(hasNoValueOrEmpty(customerAllergies) ? '' : customerAllergies)
    setMedicalHistory(hasNoValueOrEmpty(customerMedicalConditions) ? '' : customerMedicalConditions)
    setOtherQuestions(hasNoValueOrEmpty(A2) ? '' : A2)
    setExistingMedicalRecord(A3)
    // only running this once on component load w/ empty array
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        sx={{ backgroundColor: '#E2FAE3' }}
        borderRadius="8px"
        padding="12px"
      >
        <img src={`${CDN_PATH}/images/asset-lmn-card-heart.svg`} alt="Card with heart above" height="35" />
        <Typography variant="body3" marginLeft="12px">
          <b>You&apos;re almost there! </b>
          You&apos;ll be putting those pre-tax dollars to use. The following questions will help
          our healthcare provider determine your LMN eligibility.
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        paddingY="20px"
      >
        <Typography variant="h4Bold">
          Medical Profile
        </Typography>
        <Typography variant="body3" color="gray1.main">
          Please answer the following questions to the best of your ability.
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
      >
        <Typography variant="body3Bold" marginBottom="8px">
          {Q1}
        </Typography>
        <TextField
          error={error && preliminaryAssessmentConditionsInvalid}
          helperText={(error && preliminaryAssessmentConditionsInvalid) && 'Please answer the question.'}
          multiline
          size="small"
          rows={2}
          value={preliminaryAssessmentConditions}
          inputProps={{
            maxLength: 350,
          }}
          onChange={handleSetPreliminaryAssessmentConditions}
          className={styles.lmnTextFieldHelper}
        />
        <Typography variant="body3Bold" margin="8px 0">
          {Q3}
        </Typography>
        <TextField
          error={error && existingMedicalRecordInvalid}
          helperText={(error && existingMedicalRecordInvalid) && 'Please answer the question.'}
          multiline
          size="small"
          rows={2}
          value={existingMedicalRecord}
          inputProps={{
            maxLength: 350,
          }}
          onChange={handleSetExistingMedicalRecord}
          className={styles.lmnTextFieldHelper}
        />
        <Typography variant="body3Bold" margin="8px 0">
          Is there anything else we should know about your medical history?
        </Typography>
        <FormControl size="small">
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            value={hasMedicalHistory}
            onChange={(event) => handleSelectChanged(event, 'hasMedicalHistory')}
          >
            <FormControlLabel value={NO_VALUE} control={<Radio size="small" />} label={NO_VALUE} />
            <FormControlLabel value={YES_VALUE} control={<Radio size="small" />} label={YES_VALUE} />
          </RadioGroup>
        </FormControl>
        {hasMedicalHistory === YES_VALUE ? (
          <TextField
            error={error && medicalHistoryInvalid}
            helperText={(error && medicalHistoryInvalid) && 'Please answer the question.'}
            multiline
            size="small"
            rows={2}
            value={medicalHistory}
            onChange={handleSetMedicalHistory}
            className={styles.lmnTextFieldHelper}
          />
        ) : null}
        <Typography variant="body3Bold" marginBottom="8px">
          Do you have any allergies or reactions to any medication?
        </Typography>
        <FormControl size="small">
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            value={hasAllergiesOrReactions}
            onChange={(event) => handleSelectChanged(event, 'hasAllergiesOrReactions')}
          >
            <FormControlLabel value={NO_VALUE} control={<Radio size="small" />} label={NO_VALUE} />
            <FormControlLabel value={YES_VALUE} control={<Radio size="small" />} label={YES_VALUE} />
          </RadioGroup>
        </FormControl>
        {hasAllergiesOrReactions === YES_VALUE ? (
          <TextField
            error={error && allergiesOrReactionsInvalid}
            helperText={(error && allergiesOrReactionsInvalid) && 'Please answer the question.'}
            multiline
            size="small"
            rows={2}
            value={allergiesOrReactions}
            onChange={handleSetAllergiesOrReactions}
            className={styles.lmnTextFieldHelper}
          />
        ) : null}
        <Typography variant="body3Bold" marginBottom="8px">
          Do you take any over-the-counter or prescription medication,
          including birth control or herbal supplements?
        </Typography>
        <FormControl size="small">
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            value={hasSelfReportedMeds}
            onChange={(event) => handleSelectChanged(event, 'hasSelfReportedMeds')}
          >
            <FormControlLabel value={NO_VALUE} control={<Radio size="small" />} label={NO_VALUE} />
            <FormControlLabel value={YES_VALUE} control={<Radio size="small" />} label={YES_VALUE} />
          </RadioGroup>
        </FormControl>
        {hasSelfReportedMeds === YES_VALUE ? (
          <TextField
            error={error && selfReportedMedsInvalid}
            helperText={(error && selfReportedMedsInvalid) && 'Please answer the question.'}
            multiline
            size="small"
            rows={2}
            value={selfReportedMeds}
            onChange={handleSetSelfReportedMeds}
            className={styles.lmnTextFieldHelper}
          />
        ) : null}
        <Typography variant="body3Bold" marginBottom="8px">
          {Q2}
        </Typography>
        <FormControl size="small">
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            value={hasOtherQuestions}
            onChange={(event) => handleSelectChanged(event, 'hasOtherQuestions')}
          >
            <FormControlLabel value={NO_VALUE} control={<Radio size="small" />} label="No" />
            <FormControlLabel value={YES_VALUE} control={<Radio size="small" />} label="Yes" />
          </RadioGroup>
        </FormControl>
        {hasOtherQuestions === YES_VALUE ? (
          <TextField
            error={error && otherQuestionsInvalid}
            helperText={(error && otherQuestionsInvalid) && 'Please answer the question.'}
            multiline
            size="small"
            rows={2}
            value={otherQuestions}
            onChange={handleSetOtherQuestions}
            className={styles.lmnTextFieldHelper}
          />
        ) : null}
        <Button
          className={styles.lmnButton}
          color="primary"
          fullWidth
          size="large"
          variant="contained"
          onClick={handleNextStepClicked}
        >
          <Typography variant="body3Bold">
            Continue
          </Typography>
        </Button>
        <LmnBackButton />
      </Box>
    </>
  )
}

export default LmnMedicalProfile
